// The website's «Pages» tab, first view: every page that was ever analysed, once each, with its latest result. An
// analysis of a single page (a check of corrections) renews that page and leaves the others as they were, so the list
// stays whole. Each page is the same card as in «Análisis»; the whole card leads to the page's own problems.
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SitePage, sitePagesQuery } from '../api';
import { scannedFamilies } from '../page-families';
import { usePersisted } from '../persist';
import { type PageSort, sortPages } from '../results-logic';
import { Field, Message } from '../ui';
import { PageFamilyCard } from './scan-parts';

export type PageFilter = 'todas' | 'pendientes' | 'limpias';
const FILTERS: PageFilter[] = ['todas', 'pendientes', 'limpias'];
export const PAGE_SORTS: PageSort[] = ['prioridad', 'pendientes', 'fecha', 'nombre'];
/** From this many pages the list can be searched. */
const SEARCH_FROM = 8;

/** The order of the pages, kept (with the filter) for the website: coming back from a page finds the list as it was. */
export const usePageSort = (siteId: string) => usePersisted<PageSort>(`pagesort:site:${siteId}`, 'prioridad');

/** A page has pending problems, or has none, or could not be read. */
export function pageFilterMatches(page: SitePage, filter: PageFilter): boolean {
  if (filter === 'todas') return true;
  const unread = Boolean(page.blocked || page.error);
  const pending = (page.found?.problems ?? 0) + (page.found?.delegated ?? 0);
  return filter === 'pendientes' ? unread || pending > 0 : !unread && pending === 0;
}

export function PageSortSelect({
  value,
  onChange,
  id,
}: {
  value: PageSort;
  onChange: (value: PageSort) => void;
  id: string;
}) {
  const { t } = useTranslation();
  return (
    <label className="page-sort" htmlFor={id}>
      <span>{t('scan.pages.sort.label')}</span>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value as PageSort)}
        className="page-sort-select min-h-11 w-full rounded-md border border-field-border py-2 text-ink"
      >
        {PAGE_SORTS.map((mode) => (
          <option key={mode} value={mode}>
            {t(`scan.pages.sort.${mode}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The foot of a card: what a check confirmed and when the page was last analysed. */
function CardFoot({ page }: { page: SitePage }) {
  const { t, i18n } = useTranslation();
  // The day, as the website writes dates (16/10/2026): the hour of an analysis says nothing here.
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(new Date(iso));
  return (
    <ul className="page-card-foot">
      {page.verified > 0 && <li>{t('pagesList.verified', { count: page.verified })}</li>}
      <li>{t('pagesList.lastAnalysis', { date: date(page.analysedAt) })}</li>
    </ul>
  );
}

export function PagesList({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const pages = useQuery(sitePagesQuery(siteId));
  const [sort, setSort] = usePageSort(siteId);
  const [filter, setFilter] = usePersisted<PageFilter>(`pagefilter:site:${siteId}`, 'todas');
  const [query, setQuery] = useState('');
  const list = pages.data ?? [];
  const found = useMemo(
    () => new Map(list.flatMap((page) => (page.found ? [[page.key, page.found] as const] : []))),
    [list],
  );
  const byKey = useMemo(() => new Map(list.map((page) => [page.key, page])), [list]);
  const needle = query.trim().toLocaleLowerCase();
  const withProblems = list.filter((page) => pageFilterMatches(page, 'pendientes')).length;
  // The filter only helps when both groups exist; otherwise every choice shows the same list (or none).
  const filterUseful = withProblems > 0 && withProblems < list.length;
  const activeFilter = filterUseful ? filter : 'todas';
  const shown = useMemo(
    () =>
      sortPages(list, found, sort).filter(
        (page) =>
          pageFilterMatches(page, activeFilter) &&
          (!needle || `${page.title ?? ''} ${page.finalUrl}`.toLocaleLowerCase().includes(needle)),
      ),
    [list, found, sort, activeFilter, needle],
  );
  // The language versions of a page are one card, as in «Análisis».
  const families = useMemo(() => scannedFamilies(shown), [shown]);

  if (pages.isPending) return <p role="status">{t('app.loading')}</p>;
  if (pages.isError) return <Message tone="error">{t('errors.generic')}</Message>;
  if (list.length === 0)
    return (
      <div className="pages-list">
        <p>{t('pagesList.empty')}</p>
        <p className="mt-4">
          <Link to="/sites/$siteId" params={{ siteId }}>
            {t('scan.back')}
          </Link>
        </p>
      </div>
    );
  return (
    <section className="pages-list" aria-labelledby="paginas-titulo">
      <h2 id="paginas-titulo" className="text-2xl font-bold">
        {t('pagesList.title')}
      </h2>
      <p className="text-ink-muted">{t('pagesList.intro')}</p>
      <dl className="summary-figure-cards">
        <div>
          <dt>{t('pagesList.figures.analysed')}</dt>
          <dd>
            <strong>{list.length}</strong>
          </dd>
        </div>
        <div data-tone={withProblems > 0 ? 'critica' : undefined}>
          <dt>{t('pagesList.figures.pending')}</dt>
          <dd>
            <strong>{withProblems}</strong>
          </dd>
        </div>
        <div>
          <dt>{t('pagesList.figures.clean')}</dt>
          <dd>
            <strong>{list.length - withProblems}</strong>
          </dd>
        </div>
      </dl>
      <div className="pages-list-controls">
        <PageSortSelect value={sort} onChange={setSort} id="orden-paginas" />
        {filterUseful && (
          <label className="page-sort" htmlFor="filtro-paginas">
            <span>{t('pagesList.filter.label')}</span>
            <select
              id="filtro-paginas"
              value={filter}
              onChange={(event) => setFilter(event.target.value as PageFilter)}
              className="page-sort-select min-h-11 w-full rounded-md border border-field-border py-2 text-ink"
            >
              {FILTERS.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`pagesList.filter.${mode}`)}
                </option>
              ))}
            </select>
          </label>
        )}
        {list.length >= SEARCH_FROM && (
          <div className="pages-list-search">
            <Field
              type="search"
              label={t('scan.pages.search')}
              placeholder={t('scan.pages.search')}
              hideLabel
              value={query}
              onChange={setQuery}
            />
          </div>
        )}
      </div>
      <p className="sr-only" role="status">
        {t('scan.pages.found', { count: shown.length })}
      </p>
      {shown.length === 0 ? (
        <p>{t('pagesList.noMatch')}</p>
      ) : (
        <ul className="page-cards">
          {families.map((family) => (
            <li key={family.key}>
              <PageFamilyCard
                scanOf={(key) => byKey.get(key)?.scanId ?? ''}
                problemKeyOf={(key) => byKey.get(key)?.topKey}
                siteId={siteId}
                family={family}
                found={(key) => byKey.get(key)?.found ?? undefined}
                statusText={(status, pageFound) =>
                  pageFound && pageFound.problems > 0
                    ? t('pagesList.pending', { count: pageFound.problems })
                    : pageFound && pageFound.problems === 0 && pageFound.delegated === 0
                      ? t('pagesList.none')
                      : status
                }
                footer={(page) => {
                  const entry = byKey.get(page.key);
                  return entry ? <CardFoot page={entry} /> : null;
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
